import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Users, Calendar, BarChart3, Palette, Zap, Shield, Clock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { useState } from 'react';

const professionalPlan = {
  id: 'professional',
  name: 'Event Planner Pro',
  price: '$15.99',
  priceId: 'price_1RrwqFAX6lBz9Nrz5WrpqptA',
  description: 'Everything you need to manage professional events seamlessly',
  features: [
    'Unlimited events & guests',
    'Client collaboration portal',
    'Professional event templates',
    'Unlimited custom RSVP forms',
    'White-label branding options',
    'Advanced analytics & CSV export',
    'Priority support & training',
    'Calendar integrations',
    'Real-time availability tracking',
    'Automated reminder system',
  ],
};

export default function EventPlannerPro() {
  const { subscribed, subscription_tier, createCheckout, openCustomerPortal } = useSubscription();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleUpgrade = async () => {
    setLoading(true);
    await createCheckout(professionalPlan.priceId);
    setLoading(false);
  };

  const isCurrentPlan = subscribed && subscription_tier === 'professional';

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-primary">
              EventDatePicker
            </Link>
            <Link to="/auth">
              <Button variant="outline">Sign In</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            For Professional Event Planners
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 max-w-4xl mx-auto leading-tight">
            <strong>Professional Event Planning</strong> Made Effortless
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            Streamline your <strong>professional event coordination</strong> with advanced scheduling tools, <strong>client collaboration features</strong>, and powerful analytics designed for event planning professionals.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            {isCurrentPlan ? (
              <Button size="lg" onClick={openCustomerPortal} className="text-lg px-8 py-4">
                Manage Subscription
              </Button>
            ) : (
              <Button size="lg" onClick={() => navigate('/auth')} className="text-lg px-8 py-4">
                Start Free Trial
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/10">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Built for <strong>Professional Event Planners</strong>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Everything you need to manage multiple clients, coordinate complex events, and deliver exceptional results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="h-full">
              <CardHeader>
                <Users className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Client Collaboration Portal</CardTitle>
                <CardDescription>
                  Give clients direct access to vote and provide input while maintaining professional oversight.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="h-full">
              <CardHeader>
                <Calendar className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Professional Templates</CardTitle>
                <CardDescription>
                  Industry-specific templates for corporate events, weddings, conferences, and more.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="h-full">
              <CardHeader>
                <BarChart3 className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Advanced Analytics</CardTitle>
                <CardDescription>
                  Detailed insights on guest preferences, response times, and optimal scheduling patterns.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="h-full">
              <CardHeader>
                <Palette className="h-8 w-8 text-primary mb-2" />
                <CardTitle>White-Label Branding</CardTitle>
                <CardDescription>
                  Customize the platform with your brand colors, logo, and professional identity.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="h-full">
              <CardHeader>
                <Clock className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Automated Workflows</CardTitle>
                <CardDescription>
                  Set up automated reminders, follow-ups, and deadline notifications for efficient management.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="h-full">
              <CardHeader>
                <Shield className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Priority Support</CardTitle>
                <CardDescription>
                  Direct access to our support team with guaranteed response times and professional training.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Simple, Professional Pricing
            </h2>
            <p className="text-xl text-muted-foreground">
              One comprehensive plan with everything you need to succeed as a professional event planner.
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <Card className="relative border-primary shadow-lg">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                Professional Plan
              </Badge>
              
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4">
                  <div className="p-3 rounded-full bg-primary/10">
                    <Crown className="h-8 w-8 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-3xl">{professionalPlan.name}</CardTitle>
                <CardDescription className="text-lg">{professionalPlan.description}</CardDescription>
                <div className="text-4xl font-bold text-foreground">
                  {professionalPlan.price}
                  <span className="text-lg text-muted-foreground">/month</span>
                </div>
              </CardHeader>

              <CardContent>
                <ul className="space-y-4">
                  {professionalPlan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  {isCurrentPlan ? (
                    <div className="space-y-2">
                      <Button variant="outline" className="w-full" disabled>
                        Current Plan
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        onClick={openCustomerPortal}
                      >
                        Manage Subscription
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className="w-full"
                      size="lg"
                      onClick={() => navigate('/auth')}
                    >
                      Start 14-Day Free Trial
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground">
              14-day free trial • No setup fees • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-muted/10">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Ready to Elevate Your Event Planning?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of professional event planners who trust EventDatePicker to streamline their workflow and delight their clients.
          </p>
          
          <Button size="lg" onClick={() => navigate('/auth')} className="text-lg px-8 py-4">
            Start Your Free Trial
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8 px-4">
        <div className="container mx-auto text-center">
          <p className="text-muted-foreground">
            © 2024 EventDatePicker. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}